import { Box, Image, Text } from '@zillow/constellation';
import { Source, withPreload } from '@zillow/zrs-cms';
import React from 'react';

import { Post } from '../../types/shared';

interface PostFeaturedImageProps {
    post: Post;
}

const ImagePreload = withPreload(Image);
const SourcePreload = withPreload(Source);

const PostFeaturedImage: React.FC<PostFeaturedImageProps> = ({ post }) => {
    const mediumImage = post.featuredImage?.mediaDetails?.sizes?.medium;
    const largeImage = post.featuredImage?.mediaDetails?.sizes?.large;
    const fullImage = post.featuredImage
        ? {
              sourceUrl: post.featuredImage.sourceUrl,
              width: post.featuredImage.mediaDetails.width,
              height: post.featuredImage.mediaDetails.height,
          }
        : null;

    if (!post.featuredImage || (!mediumImage && !largeImage && !fullImage)) {
        return null;
    }

    return (
        <>
            <Box sx={{ textAlign: 'center' }}>
                <Box
                    as="picture"
                    sx={{
                        'img, source': {
                            borderRadius: '$radii.xxl',
                            overflow: 'hidden',
                            marginBottom: '$spacing.xs',
                            aspectRatio: '16 / 9',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            height: 'auto',
                            width: '100%',
                        },
                    }}
                >
                    {!!mediumImage && (
                        <SourcePreload
                            srcSet={mediumImage?.sourceUrl}
                            media="md_lte"
                            width={mediumImage.width}
                            height={mediumImage.height}
                        />
                    )}
                    {!!largeImage && (
                        <ImagePreload
                            src={
                                largeImage?.sourceUrl || mediumImage?.sourceUrl
                            }
                            alt={
                                post.featuredImage.altText ||
                                post.featuredImage.caption?.raw ||
                                post.title?.raw
                            }
                            width={largeImage.width}
                            height={largeImage.height}
                        />
                    )}
                    {!largeImage && !!fullImage && (
                        <ImagePreload
                            src={fullImage.sourceUrl}
                            width={fullImage.width}
                            height={fullImage.height}
                            alt={
                                post.featuredImage.altText ||
                                post.featuredImage.caption?.raw ||
                                post.title?.raw
                            }
                        />
                    )}
                </Box>
            </Box>
            {!!post.featuredImage?.caption?.raw && (
                <Box sx={{ '@media $xxl': { textAlign: 'center' } }}>
                    <Text
                        as="p"
                        fontColor="textLight"
                        fontType="bodySmall"
                        dangerouslySetInnerHTML={{
                            __html: post.featuredImage.caption.raw,
                        }}
                    />
                </Box>
            )}
        </>
    );
};

export default PostFeaturedImage;
